/**
 * Générateur — Produire une formule simple représentant la dépendance de deux
 * grandeurs.
 * Attendu ID : 994 | Niveau : 5ème | Domaine : Fonctions (cluster 2, O14)
 *
 * Plan de cartes validé (doc prof TANOED EXERCISEUR, Fonctions cluster 2) :
 *   - notations (Ex.2 « Les pommes » — les trois registres d'écriture ; les
 *     notations sont intapables en trou libre → QCM imposé par le doc) :
 *       notations_unitaire_saisie (a), notations_operation_qcm (b),
 *       notations_relation_qcm (c — distracteur majeur : l'inversion x = k×y),
 *       notations_fonctionnelle_qcm (d), notations_flechee_saisie (e),
 *       notations_relier (p(n) ↔ valeurs)
 *   - geometrie (Créé n°50 — le A(c) = c² du cadrage) : geo_formule_qcm,
 *       geo_calcul_saisie, geo_interpretation_qcm, geo_vf
 *   - production (cadrage : double, triple, moitié, successeur, carré) :
 *       production_qcm_unique, production_trou_drag, production_relier
 *
 * Invariants : coefficients à UNE décimale (dixièmes entiers, produits exacts) ;
 * un seul « × » explicite dans toutes les écritures ; distracteur d'inversion
 * (x = k × y) systématiquement présent dans les relations.
 * Variantes estimées : > 1500.
 */

import {
    randInt, randomChoice, randomChoiceMultiple,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier,
    filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 994;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Dixièmes entiers → string français. */
const fmtT = (t) => {
    const int = Math.trunc(t / 10);
    const dec = t % 10;
    return dec === 0 ? String(int) : `${int},${dec}`;
};

// ═══════════════════════════════════════════════════════════
// 1. NOTATIONS — les trois registres sur un contexte prix/quantité (Ex.2)
// ═══════════════════════════════════════════════════════════

const CONTEXTES = [
    { produit: 'de pommes', unite: 'kg', uniteLong: 'kilogramme' },
    { produit: 'd’essence', unite: 'L', uniteLong: 'litre' },
    { produit: 'de tissu', unite: 'm', uniteLong: 'mètre' },
    { produit: 'de farine', unite: 'kg', uniteLong: 'kilogramme' },
    { produit: 'de ruban', unite: 'm', uniteLong: 'mètre' },
    { produit: 'de fromage', unite: 'kg', uniteLong: 'kilogramme' },
];

/** Tire un contexte : 2 unités coûtent p2 € → coefficient k = p2/2 (1 décimale). */
const tirerContexte = () => {
    const ctx = randomChoice(CONTEXTES);
    const p2 = randomChoice([3, 5, 7, 9, 11]);
    const k10 = p2 * 5; // p2 / 2 en dixièmes
    return { ...ctx, p2, k10, k: fmtT(p2 * 5) };
};

const ENONCE = (c) =>
    `<p>En promotion, 2 ${c.unite} ${c.produit} coûtent ${c.p2} €. On note x la quantité (en ${c.unite}) et y le prix (en €).</p>`;

const genNotationsUnitaireSaisie = () => {
    const c = tirerContexte();
    return makeSaisie(c.k,
        `${ENONCE(c)}\n<p>Quel est le prix d’un ${c.uniteLong} ${c.produit} ?</p>`,
        { ...opts('Calculer le prix unitaire'), suffixe: '€' });
};

const genNotationsOperationQcm = () => {
    const c = tirerContexte();
    const bonne = `On multiplie x par ${c.k}`;
    const dist = [
        `On ajoute ${c.k} à x`,
        `On multiplie x par ${c.p2}`,
        `On divise x par ${c.k}`,
    ];
    return makeQcm([bonne, ...dist], bonne,
        `${ENONCE(c)}\n<p>Quelle opération permet d’obtenir le prix y à partir de la quantité x ?</p>`,
        opts('Choisir l’opération'));
};

const genNotationsRelationQcm = () => {
    const c = tirerContexte();
    const bonne = `y = ${c.k} × x`;
    const dist = [
        `y = x + ${c.k}`,
        `x = ${c.k} × y`,
        `y = ${c.p2} × x`,
    ];
    return makeQcm([bonne, ...dist], bonne,
        `${ENONCE(c)}\n<p>Quelle relation lie x et y ?</p>`,
        opts('Choisir la relation entre x et y'));
};

const genNotationsFonctionnelleQcm = () => {
    const c = tirerContexte();
    const bonne = `p(x) = ${c.k} × x`;
    const dist = [
        `p(x) = x + ${c.k}`,
        `x(p) = ${c.k} × p`,
        `p(x) = ${c.p2} × x`,
    ];
    return makeQcm([bonne, ...dist], bonne,
        `${ENONCE(c)}\n<p>En notation fonctionnelle, comment s’écrit le prix p en fonction de x ?</p>`,
        opts('Choisir la notation fonctionnelle'));
};

const genNotationsFlecheeSaisie = () => {
    const c = tirerContexte();
    const n = randInt(3, 8);
    return makeSaisie(fmtT(n * c.k10),
        `${ENONCE(c)}\n<p>En notation fléchée, la situation s’écrit x ↦ ${c.k} × x. Quelle est l’image de x = ${n} ?</p>`,
        { ...opts('Calculer avec la notation fléchée'), suffixe: '€' });
};

const genNotationsRelier = () => {
    const c = tirerContexte();
    const ns = randomChoiceMultiple([2, 3, 4, 5, 6, 8], 3).sort((a, b) => a - b);
    const pairs = ns.map((n) => ({ source: `p(${n})`, target: `${fmtT(n * c.k10)} €` }));
    return makeRelier(pairs,
        `${ENONCE(c)}\n<p>Le prix est donné par p(x) = ${c.k} × x. Relie chaque écriture à sa valeur.</p>`,
        opts('Relier p(n) à sa valeur'));
};

// ═══════════════════════════════════════════════════════════
// 2. GEOMETRIE — produire des formules géométriques (Créé n°50)
// ═══════════════════════════════════════════════════════════

// Objets linéaires (formule f(c) = k × c) et le carré (A(c) = c × c)
const OBJETS_LIN = [
    { desc: 'le périmètre P d’un carré de côté c', fn: 'P', variable: 'c', k: 4 },
    { desc: 'le périmètre P d’un triangle équilatéral de côté c', fn: 'P', variable: 'c', k: 3 },
    { desc: 'le périmètre P d’un pentagone régulier de côté c', fn: 'P', variable: 'c', k: 5 },
    { desc: 'le périmètre P d’un hexagone régulier de côté c', fn: 'P', variable: 'c', k: 6 },
    { desc: 'le périmètre P d’un octogone régulier de côté c', fn: 'P', variable: 'c', k: 8 },
    { desc: 'le périmètre P d’un losange de côté c', fn: 'P', variable: 'c', k: 4 },
    { desc: 'l’aire S d’un rectangle de largeur 2 cm, en fonction de sa longueur L', fn: 'S', variable: 'L', k: 2 },
    { desc: 'l’aire S d’un rectangle de largeur 3 cm, en fonction de sa longueur L', fn: 'S', variable: 'L', k: 3 },
    { desc: 'l’aire S d’un rectangle de largeur 5 cm, en fonction de sa longueur L', fn: 'S', variable: 'L', k: 5 },
    { desc: 'l’aire S d’un rectangle de largeur 6 cm, en fonction de sa longueur L', fn: 'S', variable: 'L', k: 6 },
    { desc: 'l’aire S d’un rectangle de largeur 7 cm, en fonction de sa longueur L', fn: 'S', variable: 'L', k: 7 },
];

const genGeoFormuleQcm = () => {
    const consigne = randomChoice(['Exprime', 'Donne la formule qui exprime']);
    if (randInt(0, 3) === 0) {
        // Le carré du cadrage : A(c) = c × c
        const bonne = 'A(c) = c × c';
        const dist = ['A(c) = 4 × c', 'A(c) = 2 × c', 'A(c) = c + c'];
        return makeQcm([bonne, ...dist], bonne,
            `<p>${consigne} l’aire A d’un carré en fonction de son côté c (notation fonctionnelle).</p>`,
            opts('Produire la formule de l’aire du carré'));
    }
    const o = randomChoice(OBJETS_LIN);
    const v = o.variable;
    const bonne = `${o.fn}(${v}) = ${o.k} × ${v}`;
    const dist = [
        `${o.fn}(${v}) = ${v} + ${o.k}`,
        `${o.fn}(${v}) = ${v} × ${v}`,
        `${o.fn}(${v}) = ${o.k + 2} × ${v}`,
    ];
    return makeQcm([bonne, ...dist], bonne,
        `<p>${consigne} ${o.desc} (notation fonctionnelle).</p>`,
        opts('Produire une formule géométrique'));
};

const genGeoCalculSaisie = () => {
    if (randInt(0, 2) === 0) {
        const c = randInt(2, 9);
        return makeSaisie(String(c * c),
            `<p>L’aire d’un carré de côté c est donnée par A(c) = c × c. Calcule A(${c}).</p>`,
            { ...opts('Calculer A(c)'), suffixe: 'cm²' });
    }
    const o = randomChoice(OBJETS_LIN);
    const v10 = randomChoice([20, 30, 40, 50, 60, 70, 25, 45, 65]); // valeurs à 1 décimale max
    return makeSaisie(fmtT(o.k * v10),
        `<p>On a ${o.desc} : ${o.fn}(${o.variable}) = ${o.k} × ${o.variable}. Calcule ${o.fn}(${fmtT(v10)}).</p>`,
        { ...opts('Calculer f(x) avec la formule'), suffixe: o.fn === 'S' ? 'cm²' : 'cm' });
};

const genGeoInterpretationQcm = () => {
    const c = randInt(3, 9);
    const variante = randInt(0, 2);
    if (variante === 0) {
        const bonne = `L’aire d’un carré de côté ${c} cm est ${c * c} cm²`;
        const dist = [
            `Le périmètre d’un carré de côté ${c} cm est ${c * c} cm`,
            `Le côté d’un carré d’aire ${c} cm² est ${c * c} cm`,
            `${c} carrés ont une aire totale de ${c * c} cm²`,
        ];
        return makeQcm([bonne, ...dist], bonne,
            `<p>Pour l’aire du carré, on note A(c) = c × c. Que signifie l’égalité A(${c}) = ${c * c} ?</p>`,
            opts('Interpréter une écriture fonctionnelle'));
    }
    if (variante === 1) {
        const bonne = `Le périmètre d’un carré de côté ${c} cm est ${4 * c} cm`;
        const dist = [
            `L’aire d’un carré de côté ${c} cm est ${4 * c} cm²`,
            `Le côté d’un carré de périmètre ${c} cm est ${4 * c} cm`,
            `4 carrés de côté ${c} cm ont un périmètre total de ${4 * c} cm`,
        ];
        return makeQcm([bonne, ...dist], bonne,
            `<p>Pour le périmètre du carré, on note P(c) = 4 × c. Que signifie l’égalité P(${c}) = ${4 * c} ?</p>`,
            opts('Interpréter une écriture fonctionnelle'));
    }
    const a = randomChoice([2, 3, 5]);
    const bonne = `L’aire d’un rectangle de largeur ${a} cm et de longueur ${c} cm est ${a * c} cm²`;
    const dist = [
        `Le périmètre de ce rectangle est ${a * c} cm`,
        `La longueur d’un rectangle d’aire ${a} cm² est ${a * c} cm`,
        `${a} rectangles de longueur ${c} cm ont une aire totale de ${a * c} cm²`,
    ];
    return makeQcm([bonne, ...dist], bonne,
        `<p>Pour des rectangles de largeur ${a} cm, on note S(L) = ${a} × L. Que signifie l’égalité S(${c}) = ${a * c} ?</p>`,
        opts('Interpréter une écriture fonctionnelle'));
};

const genGeoVF = () => {
    const c = randInt(3, 9);
    const o = randomChoice(OBJETS_LIN);
    const pool = [
        [`P(c) = 4 × c signifie que le périmètre d’un carré est proportionnel à son côté`, 0],
        [`A(c) = c × c signifie que l’aire d’un carré est le double de son côté`, 1],
        [`Pour un carré, A(${c}) = ${c * c}`, 0],
        [`Pour un carré, P(${c}) = ${4 * c + randomChoice([2, 4])}`, 1],
        [`Avec ${o.fn}(${o.variable}) = ${o.k} × ${o.variable}, on a ${o.fn}(${c}) = ${o.k * c}`, 0],
        [`Pour un carré, A(${c}) = ${4 * c}`, c * c === 4 * c ? 0 : 1],
    ];
    const lignes = randomChoiceMultiple(pool, 4);
    return makeVraiFaux(lignes.map((l) => l[0]), lignes.map((l) => l[1]),
        `<p>On note P(c) le périmètre et A(c) l’aire d’un carré de côté c (en cm). Vrai ou faux ?</p>`,
        opts('Vrai/faux sur les écritures fonctionnelles'));
};

// ═══════════════════════════════════════════════════════════
// 3. PRODUCTION — de la situation à la formule (cadrage : double, carré…)
// ═══════════════════════════════════════════════════════════

const SITUATIONS = [
    { texte: 'y est le double de x', formule: 'y = 2 × x' },
    { texte: 'y est le triple de x', formule: 'y = 3 × x' },
    { texte: 'y est la moitié de x', formule: 'y = x ÷ 2' },
    { texte: 'y est le successeur de x', formule: 'y = x + 1' },
    { texte: 'y est le carré de x', formule: 'y = x × x' },
    { texte: 'y est le quadruple de x', formule: 'y = 4 × x' },
    { texte: 'y est le tiers de x', formule: 'y = x ÷ 3' },
    { texte: 'y est le quintuple de x', formule: 'y = 5 × x' },
];

const genProductionQcm = () => {
    const choisis = randomChoiceMultiple(SITUATIONS, 4);
    const cible = randomChoice(choisis);
    return makeQcm(choisis.map((s) => s.formule), cible.formule,
        `<p>Quelle formule traduit la situation : « ${cible.texte} » ?</p>`,
        opts('Choisir la formule d’une situation'));
};

const genProductionTrouDrag = () => {
    const objet = randomChoice([
        { sing: 'stylo', plur: 'stylos' },
        { sing: 'croissant', plur: 'croissants' },
        { sing: 'carnet', plur: 'carnets' },
        { sing: 'billet de cinéma', plur: 'billets de cinéma' },
    ]);
    const k = randInt(2, 9);
    const phrase = `Le prix de x ${objet.plur} est y = {{${k}}} × x.`;
    const candidats = [String(k + 1), String(k - 1), String(2 * k), String(k + 10)];
    const distracteurs = filtrerDistracteurs(phrase, candidats).slice(0, 3);
    return makeTexteTrou(
        phrase,
        `<p>Un ${objet.sing} coûte ${k} €. On note x le nombre de ${objet.plur} achetés et y le prix total (en €). Complète la formule.</p>`,
        { mode: 'drag', distracteurs, ...opts('Compléter la formule du prix') },
    );
};

const genProductionRelier = () => {
    const choisis = randomChoiceMultiple(SITUATIONS, 3);
    const pairs = choisis.map((s) => ({ source: s.texte, target: s.formule }));
    return makeRelier(pairs,
        `<p>Relie chaque situation à la formule qui la traduit.</p>`,
        opts('Relier situations et formules'));
};

// ═══════════════════════════════════════════════════════════
// Registre + contrat générateur standard
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    notations_unitaire_saisie: genNotationsUnitaireSaisie,
    notations_operation_qcm: genNotationsOperationQcm,
    notations_relation_qcm: genNotationsRelationQcm,
    notations_fonctionnelle_qcm: genNotationsFonctionnelleQcm,
    notations_flechee_saisie: genNotationsFlecheeSaisie,
    notations_relier: genNotationsRelier,

    geo_formule_qcm: genGeoFormuleQcm,
    geo_calcul_saisie: genGeoCalculSaisie,
    geo_interpretation_qcm: genGeoInterpretationQcm,
    geo_vf: genGeoVF,

    production_qcm_unique: genProductionQcm,
    production_trou_drag: genProductionTrouDrag,
    production_relier: genProductionRelier,
};

const FORMAT_LABELS = {
    unitaire_saisie: 'Prix unitaire (saisie)',
    operation_qcm: 'Choisir l’opération',
    relation_qcm: 'Relation y = k × x',
    fonctionnelle_qcm: 'Notation fonctionnelle',
    flechee_saisie: 'Notation fléchée (saisie)',
    relier: 'Relier',
    formule_qcm: 'Produire la formule',
    calcul_saisie: 'Calculer f(x)',
    interpretation_qcm: 'Interpréter',
    vf: 'Vrai/Faux',
    qcm_unique: 'QCM',
    trou_drag: 'Compléter (glisser)',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS)
        .filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({
            key: k,
            label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k,
        }));

export const CATEGORIES = [
    {
        id: 'notations',
        label: 'Les trois écritures d’une dépendance (relation, fonctionnelle, fléchée)',
        formatsList: buildFormats('notations'),
    },
    {
        id: 'geometrie',
        label: 'Produire des formules géométriques',
        formatsList: buildFormats('geo'),
    },
    {
        id: 'production',
        label: 'De la situation à la formule',
        formatsList: buildFormats('production'),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length
            ? randomChoice(matching)
            : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
